<template>
  <div class="row q-col-gutter-x-xl q-col-gutter-y-sm">
    <div v-if="!banks.length" class="col-12">
      <span class="text-md text-neutral">No bank account found!</span>
    </div>
    <div v-for="bank in banks" v-else :key="bank._id" class="col-12 q-mb-xs">
      <div class="flex justify-between items-center">
        <div class="flex items-center">
          <div class="text-md text-weight-medium text-neutral">
            {{ bank.bank_name }}
          </div>
          <div class="q-pl-xxs">
            <div
              v-if="
                ['awaiting_verification', 'incomplete'].includes(bank.status_cd)
              "
            >
              <ReviewIcon class="icon text-neutral-60 q-mt-xxs" />
            </div>
            <div
              v-else-if="
                bank.ai_verification?.status_cd === 'verified' &&
                bank.status_cd === 'verified'
              "
            >
              <Menu placement="bottom-start">
                <component :is="RoundCheckMagicIcon" class="icon q-mt-xxs" />
                <template #popper>
                  <span class="text-xs">AI & Penny Verified</span>
                </template>
              </Menu>
            </div>

            <div
              v-else-if="
                bank.status_cd === 'verified' &&
                bank.ai_verification?.status_cd !== 'verified'
              "
            >
              <Menu placement="bottom-start">
                <component
                  :is="RoundCheckIcon"
                  class="icon text-success-70 q-mt-xxs"
                />
                <template #popper>
                  <span class="text-xs"
                    >Penny Verified.
                    <span
                      v-if="bank.ai_verification?.reason"
                      class="text-error-60"
                    >
                      AI verification failed -
                      {{ bank.ai_verification?.reason }}</span
                    ></span
                  >
                </template>
              </Menu>
            </div>
            <div
              v-else-if="bank.status_cd === 'unverified'"
              class="flex items-center"
            >
              <Menu placement="bottom-start">
                <component
                  :is="ExclamationIcon"
                  class="icon text-error-60 q-mt-xxs"
                />
                <template #popper>
                  <div v-if="bank?.reason">
                    {{ bank.reason }}.
                    <span v-if="bank.ai_verification?.reason">{{
                      bank.ai_verification?.reason
                    }}</span>
                  </div>
                </template>
              </Menu>

              <PBtn flat label="Retry" @click="verifyBank(bank)" />
            </div>
          </div>
        </div>
        <div class="flex justify-between" :style="{ width: '65px' }">
          <PBtn
            dense
            flat
            fluid
            link
            :loading="isRemoveLoading[bank._id]"
            :disable="bank.is_primary"
            @click="remove(bank)"
          >
            <RemoveIcon class="icon" />
            <PTooltip>
              <span>
                {{
                  bank.is_primary
                    ? "You cannot remove a primary bank"
                    : "Remove"
                }}
              </span>
            </PTooltip>
          </PBtn>
          <PBtn dense flat fluid link @click="editBank(bank)">
            <EditIcon class="icon" />
            <PTooltip><span>Edit</span></PTooltip>
          </PBtn>
        </div>
      </div>
      <PBadge
        v-if="bank.is_primary"
        rounded
        class="text-sm text-weight-medium q-ma-xxs"
        :label="'Primary'"
        color="primary-30"
        text-color="primary"
      />
      <div class="row q-col-gutter-xxs q-mt-md">
        <div class="col-3">
          <span class="text-sm text-neutral-80">Beneficiary Name</span>
        </div>
        <div class="col-3">
          <span class="text-sm text-neutral text-weight-medium">
            {{ bank.bank_beneficiary_name }}
          </span>
        </div>
        <div class="col-3">
          <span class="text-sm text-neutral-80">Account Number</span>
        </div>
        <div class="col-3">
          <span class="text-sm text-neutral text-weight-medium">
            {{ bank.account_number }}
          </span>
        </div>
        <div class="col-3">
          <span class="text-sm text-neutral-80">IFSC Code</span>
        </div>
        <div class="col-3">
          <span class="text-sm text-neutral text-weight-medium">
            {{ bank.ifsc_code }}
          </span>
        </div>
        <div class="col-6"></div>

        <div class="col-3">
          <span class="text-sm text-neutral-80">Branch</span>
        </div>
        <div class="col-9">
          <span class="text-sm text-neutral text-weight-semibold">
            {{ bank.branch_address || "Not Available" }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import ReviewIcon from "assets/icons/time-circle.svg";
import RoundCheckIcon from "assets/icons/check-round-outline.svg";
import RoundCheckMagicIcon from "assets/icons/check-round-magic.svg";
import ExclamationIcon from "assets/icons/exclamation-round.svg";
import EditIcon from "assets/icons/edit.svg";
import RemoveIcon from "assets/icons/remove.svg";
import PConfirmDialog from "src/components/PConfirmDialog.vue";
import { useQuasar } from "quasar";
import { Menu } from "floating-vue";
import "floating-vue/dist/style.css";

const $q = useQuasar();
const isRemoveLoading = reactive({});
const { verifyBank, toggleAddBankForm, removeBank } = useBankStore();

defineProps({
  banks: {
    type: Array,
    required: true,
  },
});

const confirmationMessage = (bank) => {
  return `<p>
            <span class="text-weight-medium">
              Are you sure you want to remove this bank account?
            </span>
            <div class="row q-col-gutter-md">
              <div class="col-4">
                <span class="text-neutral-70 text-sm">
                  Bank Name:
                </span>
              </div>
              <div class="col-6">
                <span class="text-weight-semibold">
                   ${unref(bank)?.bank_name}
                </span>
              </div>
              <div class="col-4">
                <span class="text-neutral-70 text-sm">
                  Account Number:
                </span>
              </div>
              <div class="col-6">
                <span class="text-weight-semibold">
                  ${unref(bank)?.account_number}
                </span>
              </div>
            </div>
          </p>`;
};

const remove = async (bank) => {
  const { file, _id } = bank;
  const deleteDialog = $q.dialog({
    component: PConfirmDialog,
    componentProps: {
      title: `Remove Bank Account`,
      htmlContent: confirmationMessage(bank),
      labelClass: "q-ml-xl rounded-borders",
      isFooterVisible: true,
      submittable: true,
      cancellable: true,
      closable: true,
      onConfirm: async () => {
        try {
          deleteDialog.hide();

          unref(isRemoveLoading)[bank._id] = true;

          await removeBank({
            file_id: file?._id,
            _id,
          });

          isRemoveLoading.value = false;

          toggleAddBankForm({ open: false, level: 0 });
        } catch (error) {
          unref(isRemoveLoading)[bank._id] = false;

          console.log(error);
        }
      },
    },
  });
};

const editBank = (bank) => {
  toggleAddBankForm({
    open: true,
    level: 0,
    edit: true,
    props: {
      bank: unref(bank),
      removePopup: remove,
      isRemoveLoading,
    },
  });
};
</script>
